/**
 * Display-only ledger formatting. Does not change what the agent emitted or
 * what stays in the transcript for later turns — only how the UI presents it.
 *
 * Caller: InsightsLedgerCard. Schema: KEY: value lines from agent <ledger>
 * (QUESTION, DEFINITION, HYPOTHESES, …). User: readable notes without affecting
 * execution — no QUESTION duplication, section labels, spacing.
 */

export type LedgerDisplaySection = {
  key: string;
  label: string;
  /** Raw value after the KEY: prefix. */
  value: string;
  /** Prefer list rendering when the value is delimited with · */
  items?: string[];
};

const SECTION_LABELS: Record<string, string> = {
  QUESTION: "Question",
  DEFINITION: "Working definition",
  "ORG MAP": "Systems",
  PLAN: "Plan",
  HYPOTHESES: "Hypotheses",
  DATASETS: "Datasets",
  COVERAGE: "Coverage",
  CONFIRMED: "Confirmed",
  "DATA QUALITY": "Data quality",
  FAILED: "Failures & recovery",
  SCOPE: "Scope",
  AUDIT: "Pre-answer check",
};

const LISTISH_KEYS = new Set([
  "HYPOTHESES",
  "DATASETS",
  "ORG MAP",
  "PLAN",
  "COVERAGE",
  "CONFIRMED",
]);

function humanizeKey(key: string): string {
  const upper = key.toUpperCase();
  if (SECTION_LABELS[upper]) return SECTION_LABELS[upper];
  return key
    .toLowerCase()
    .split(/[\s_]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function splitListItems(value: string): string[] | undefined {
  if (!value.includes("·")) return undefined;
  const parts = value
    .split(/\s*·\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.length > 1 ? parts : undefined;
}

/**
 * Parse KEY: value ledger lines into display sections.
 * Skips QUESTION by default (already shown in the page / task trail).
 */
export function parseLedgerForDisplay(
  content: string,
  options?: { omitQuestion?: boolean },
): LedgerDisplaySection[] {
  const omitQuestion = options?.omitQuestion !== false;
  const lines = content.split("\n");
  const sections: LedgerDisplaySection[] = [];
  let current: LedgerDisplaySection | null = null;

  const flush = () => {
    if (!current) return;
    if (LISTISH_KEYS.has(current.key.toUpperCase()) || current.value.includes("·")) {
      current.items = splitListItems(current.value);
    }
    sections.push(current);
    current = null;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const trimmed = line.trim();
    if (!trimmed) continue;

    const m = /^([A-Z][A-Z0-9 /_-]{0,40}?):\s*(.*)$/.exec(trimmed);
    if (m) {
      flush();
      const key = m[1]!.trim();
      const value = (m[2] ?? "").trim();
      if (omitQuestion && key.toUpperCase() === "QUESTION") continue;
      current = {
        key,
        label: humanizeKey(key),
        value,
      };
      continue;
    }

    if (current) {
      current.value = `${current.value}\n${trimmed}`.trim();
    } else {
      current = { key: "NOTE", label: "Note", value: trimmed };
    }
  }
  flush();
  return sections;
}

/** Collapsed preview: first meaningful section, not QUESTION. */
export function ledgerPreviewLine(content: string): string {
  const sections = parseLedgerForDisplay(content, { omitQuestion: true });
  const first = sections[0];
  if (!first) return "Working notes";
  const body = first.items?.[0] ?? first.value;
  const text = `${first.label}: ${body}`;
  return text.length > 72 ? `${text.slice(0, 69)}…` : text;
}
